import AppComponent from '../base/app-component.ts';
import type { DataTable, ColumnDef } from './data-table.ts';
import { raccogliDateFilters, handleDateOpChange } from '../lib/date-filter.ts';
import { getCasseMap, type CassaInfo } from '../lib/casse-info.ts';

function buildColumns(casse: CassaInfo[]): ColumnDef[] {
  const fisse: ColumnDef[] = [
    { key: 'codFiscale', label: 'Codice Fiscale', type: 'code' },
    { key: 'cognome', label: 'Cognome/Nome', type: 'text' },
    { key: 'dataNascita', label: 'Data nascita', type: 'date', emptyText: '-' },
  ];
  const colonneCasse: ColumnDef[] = casse.map(c => ({
    key: `stato_${c.codice}`,
    label: c.codice,
    type: 'text',
  }));
  const code: ColumnDef[] = [
    { key: 'operatore', label: 'Operatore', type: 'text' },
    { key: 'datapres', label: 'Data', type: 'date' },
    { key: 'origine', label: 'Origine', type: 'text' },
  ];
  return [...fisse, ...colonneCasse, ...code];
}

export class Archivio extends AppComponent {
  static override componentName = 'wc-archivio';
  protected override get templateId() { return 'archivio'; }

  private dataTable: DataTable | null = null;
  private currentPage = 1;
  private currentFilter = '';
  private filtroOrigine = '';
  private casse: CassaInfo[] = [];

  protected override buildRenderingData(): Record<string, unknown> {
    const casse = Object.values(getCasseMap());
    this.casse = casse;
    return {
      ...super.buildRenderingData(),
      columnsJson: JSON.stringify(buildColumns(casse)),
      casse,
    };
  }

  protected override afterRender(): void {
    this.dataTable = this.$('wc-data-table') as DataTable | null;
    if (!this.dataTable) return;
    this.setupEvents();
    this.carica();
  }

  private setupEvents(): void {
    if (!this.dataTable) return;

    this.listen(this.dataTable, 'dt-page-change', (e: Event) => {
      this.currentPage = (e as CustomEvent).detail.page;
      this.carica();
    });

    this.listen(this.dataTable, 'dt-filter-change', (e: Event) => {
      this.currentFilter = (e as CustomEvent).detail.filter;
      this.currentPage = 1;
      this.carica();
    });

    this.listen(this.dataTable, 'dt-row-action', async (e: Event) => {
      const { action, row } = (e as CustomEvent).detail;
      if (action === 'elimina') {
        const ok = await (globalThis as any).Weext.confirm(
          `Eliminare il record archivio per ${row.codFiscale}?`,
          { title: 'Conferma eliminazione', confirmText: 'Elimina', confirmClass: 'btn-danger' }
        );
        if (!ok) return;
        const result = await this.api.archivio.elimina(Number(row.id));
        if (result.ok) {
          (globalThis as any).Weext.toast('Record eliminato', { type: 'success' });
          this.carica();
        } else {
          (globalThis as any).Weext.toast(result.error, { type: 'danger' });
        }
      }
    });

    // Filtro data nascita
    this.delegate.on('change', '[data-date-op]', (e: Event) => handleDateOpChange(this, e));
    this.delegate.on('change', '[data-filtro-op="dataNascita"]', () => {
      this.currentPage = 1;
      this.carica();
    });
    this.delegate.on('change', '[data-filtro-val="dataNascita"]', () => {
      this.currentPage = 1;
      this.carica();
    });
    this.delegate.on('change', '[data-filtro-val="dataNascitaA"]', () => {
      this.currentPage = 1;
      this.carica();
    });

    // Filtri rapidi origine
    this.delegate.on('click', '[data-filtro-rapido="origine"] button', (e: Event) => {
      const btn = (e.target as HTMLElement).closest('button') as HTMLElement;
      const group = btn.closest('[data-filtro-rapido]') as HTMLElement;
      const value = btn.dataset.value!;
      const wasActive = btn.classList.contains('active');

      group.querySelectorAll('button').forEach(b => b.classList.remove('active'));
      if (!wasActive) btn.classList.add('active');

      this.filtroOrigine = wasActive ? '' : value;
      this.currentPage = 1;
      this.carica();
    });
  }

  private async carica(): Promise<void> {
    if (!this.dataTable) return;
    this.dataTable.setLoading();

    const params: Record<string, string | number | undefined> = {
      page: this.currentPage,
      limit: 30,
      q: this.currentFilter || undefined,
      origine: this.filtroOrigine || undefined,
      ...raccogliDateFilters(this, ['dataNascita']),
    };

    const result = await this.api.archivio.lista(params);
    if (!result.ok) {
      this.dataTable.setError(result.error);
      return;
    }

    const { archivio, count, totalPages, pageSize, page } = result.data;
    const rows = archivio.map(r => {
      const codPerCassa = r.codPerCassa ?? {};
      const esaurito = this.casse.length > 0
        && this.casse.every(c => {
          const v = codPerCassa[c.codice];
          return v != null && v !== '';
        });
      return {
        ...(r as unknown as Record<string, unknown>),
        _casse: this.casse,
        _esaurito: esaurito,
      };
    });
    this.dataTable.setData(rows, {
      page, totalPages, count, pageSize,
    });
  }
}
